import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Star,
  Zap,
  Crown,
  Rocket,
  Shield,
  CheckCircle,
  Users,
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { PremiumQuoteForm } from '../components/PremiumQuoteForm';
import { useLanguage } from '../contexts/LanguageContext';

export const NewSignaturePage = () => {
  const [expandedSignature, setExpandedSignature] = useState(null);
  const [selectedTier, setSelectedTier] = useState('medium'); // small, medium, large, enterprise
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const { t } = useLanguage();

  // Tier labels (future use / kept for completeness)
  const tierLabels =
    t('signaturePage.tierLabels') || {
      small: '< 50 Mitarbeiter',
      medium: '50-250 Mitarbeiter',
      large: '250-1000 Mitarbeiter',
      enterprise: '1000+ Mitarbeiter'
    };

  // Signature programs from translations
  const rawSignatures = t('signaturePage.signatures');
  const signaturesMeta = [
    {
      id: 'resonance-foundation',
      icon: Sparkles,
      gradient: 'from-blue-600 to-cyan-600'
    },
    {
      id: 'resonance-accelerator',
      icon: Rocket,
      gradient: 'from-purple-600 to-pink-600'
    },
    {
      id: 'resonance-transformation',
      icon: Crown,
      gradient: 'from-orange-500 to-red-600'
    },
    {
      id: 'resonance-sos',
      icon: Shield,
      gradient: 'from-red-600 to-orange-600'
    }
  ];

  const signatures = Array.isArray(rawSignatures)
    ? signaturesMeta.map((meta) => {
        const sig =
          rawSignatures.find((s) => s.id === meta.id) || rawSignatures[0] || {};
        return {
          ...sig,
          icon: meta.icon,
          gradient: meta.gradient
        };
      })
    : [];

  // Individual à-la-carte services
  const rawIndividualServices = t('signaturePage.individualServices');
  const individualServices = Array.isArray(rawIndividualServices)
    ? rawIndividualServices
    : [];

  const toggleSignature = (signatureId) => {
    setExpandedSignature(
      expandedSignature === signatureId ? null : signatureId
    );
  };

  const openQuoteForm = (programTitle) => {
    setSelectedProgram(programTitle);
    setIsQuoteFormOpen(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 pt-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full border border-purple-400/30 mb-8">
            <Star className="w-4 h-4 text-purple-400 mr-2" />
            <span className="text-sm text-slate-300">
              {t('signaturePage.hero.badge')}
            </span>
          </div>

          <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
            {t('signaturePage.hero.titleLine1')}
            <br />
            <span className="gradient-text font-medium">
              {t('signaturePage.hero.titleHighlight')}
            </span>
          </h1>

          <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed font-light mb-8">
            {t('signaturePage.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Signature Programs - Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('signaturePage.signaturesSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('signaturePage.signaturesSection.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t('signaturePage.signaturesSection.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {signatures.map((signature) => (
              <div
                key={signature.id}
                className="border-2 border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
              >
                {/* Signature Header - Clickable */}
                <button
                  onClick={() => toggleSignature(signature.id)}
                  className="w-full p-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div
                      className={`w-14 h-14 rounded-xl bg-gradient-to-r ${signature.gradient} flex items-center justify-center flex-shrink-0`}
                    >
                      <signature.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-2xl font-semibold text-slate-900">
                          {signature.title}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${signature.gradient} text-white`}
                        >
                          {signature.tier}
                        </span>
                      </div>
                      <p
                        className={`text-sm bg-gradient-to-r ${signature.gradient} bg-clip-text text-transparent font-medium mb-1`}
                      >
                        {signature.subtitle}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {signature.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                  {expandedSignature === signature.id ? (
                    <ChevronUp className="w-6 h-6 text-slate-400 flex-shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-slate-400 flex-shrink-0 ml-4" />
                  )}
                </button>

                {/* Signature Content - Expandable */}
                {expandedSignature === signature.id && (
                  <div className="p-8 bg-slate-50 border-t-2 border-slate-200">
                    {/* Description */}
                    <p className="text-lg text-slate-700 leading-relaxed mb-8">
                      {signature.description}
                    </p>

                    {/* Für Wen */}
                    {signature.forWhom && (
                      <div className="mb-8 p-6 bg-white rounded-xl border-l-4 border-purple-600">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                          <Users className="w-5 h-5 text-purple-600 mr-2" />
                          {signature.forWhom.title}
                        </h4>
                        <ul className="space-y-2">
                          {Array.isArray(signature.forWhom.targets) &&
                            signature.forWhom.targets.map((target, idx) => (
                              <li
                                key={idx}
                                className="flex items-start text-slate-700"
                              >
                                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                {target}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}

                    {/* Setup / Program Structure */}
                    {signature.setup && (
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
                          <TrendingUp className="w-5 h-5 text-orange-500 mr-2" />
                          {t('signaturePage.programDetails.programFlowTitle')}
                        </h4>
                        <div className="space-y-4">
                          {Object.entries(signature.setup).map(
                            ([key, phase]) => (
                              <div
                                key={key}
                                className="bg-white p-6 rounded-xl border border-slate-200"
                              >
                                <h5 className="font-semibold text-slate-900 mb-3">
                                  {phase.title}
                                </h5>
                                <div className="grid md:grid-cols-2 gap-3 mb-3">
                                  {Array.isArray(phase.activities) &&
                                    phase.activities.map((activity, i) => (
                                      <div
                                        key={i}
                                        className="flex items-start text-sm text-slate-600"
                                      >
                                        <div
                                          className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${signature.gradient} mt-2 mr-2 flex-shrink-0`}
                                        />
                                        {activity}
                                      </div>
                                    ))}
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-200">
                                  <span className="text-xs font-semibold text-slate-500">
                                    {t(
                                      'signaturePage.programDetails.deliverableLabel'
                                    )}
                                  </span>
                                  <span className="text-sm text-slate-700 ml-2">
                                    {phase.deliverable}
                                  </span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Included Services */}
                    {Array.isArray(signature.included) && (
                      <div className="mb-8 p-6 bg-white rounded-xl border border-slate-200">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4">
                          {t('signaturePage.programDetails.includedTitle')}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {signature.included.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-start text-sm text-slate-700"
                            >
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Expected Results */}
                    {Array.isArray(signature.results) && (
                      <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border border-purple-200">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4">
                          {t('signaturePage.programDetails.resultsTitle')}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {signature.results.map((result, idx) => (
                            <div
                              key={idx}
                              className="flex items-center text-slate-700"
                            >
                              <Star className="w-4 h-4 mr-2 text-orange-500" />
                              {result}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Request Quote Button */}
                    <div className="p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border-2 border-purple-200 text-center">
                      <h4 className="text-lg font-semibold text-slate-900 mb-3">
                        {t('signaturePage.programDetails.investmentTitle')}
                      </h4>
                      <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                        {t('signaturePage.programDetails.investmentSubtitle')}
                      </p>
                      <button
                        onClick={() => openQuoteForm(signature.title)}
                        className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
                      >
                        {t('signaturePage.programDetails.requestButton')}
                      </button>
                      <p className="text-xs text-slate-500 mt-4">
                        {t('signaturePage.programDetails.requestNote')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Services Pricing */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('signaturePage.individualSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('signaturePage.individualSection.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t('signaturePage.individualSection.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {individualServices.map((service, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all duration-300 hover:border-purple-300 flex flex-col"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {service.service}
                </h3>
                <p className="text-sm text-slate-600 mb-3 flex-grow">
                  {service.description}
                </p>
                <div className="flex items-center text-sm text-slate-500 mb-4">
                  <Clock className="w-4 h-4 mr-1" />
                  {service.duration}
                </div>
                <button
                  onClick={() => openQuoteForm(service.service)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  {t('signaturePage.programDetails.requestButton')}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-white rounded-xl border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-3">
              {t(
                'signaturePage.individualSection.whyNoPublicPricesTitle'
              )}
            </h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t('signaturePage.individualSection.whyNoPublicPricesText')}
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Signatures */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('signaturePage.whySection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('signaturePage.whySection.highlight')}
              </span>
              ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Array.isArray(t('signaturePage.whySection.cards')) &&
              t('signaturePage.whySection.cards').map((card, index) => {
                const gradients = [
                  'from-purple-600 to-blue-600',
                  'from-orange-500 to-red-500',
                  'from-emerald-500 to-teal-600'
                ];
                const icons = [CheckCircle, TrendingUp, Sparkles];
                const Icon = icons[index] || CheckCircle;
                const gradient = gradients[index] || gradients[0];

                return (
                  <div
                    key={index}
                    className="text-center p-6 bg-slate-50 rounded-2xl"
                  >
                    <div
                      className={`w-16 h-16 bg-gradient-to-r ${gradient} rounded-full flex items-center justify-center mx-auto mb-4`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {card.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{card.text}</p>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light mb-6">
            {t('signaturePage.cta.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('signaturePage.cta.highlight')}
            </span>
            ?
          </h2>
          <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            {t('signaturePage.cta.text')}
          </p>
          <button
            onClick={() =>
              openQuoteForm(t('signaturePage.cta.dialogProgramLabel'))
            }
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-lg transition-all duration-300 hover:shadow-glow hover:scale-105"
          >
            <span className="mr-3">{t('signaturePage.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Quote Form Modal */}
      <PremiumQuoteForm
        isOpen={isQuoteFormOpen}
        onClose={() => setIsQuoteFormOpen(false)}
        selectedProgram={selectedProgram}
      />
    </div>
  );
};
